import { v4 as uuidv4 } from 'uuid';
import { ICONS_NAMES_GROUPED } from '../assets/shared/data/icons-name.data';
import { CALENDAR_DATE_NOTATIONS, CalendarLanguages } from '../components/_form-components/calendar/models/calendar.types';
import { BRANDS } from '../models/brands.types';
import { STORY_CATEGORIES, StoryCategories } from '../models/categories.types';
import { CONTROL_TYPES } from '../models/controls.types';
import { INDICATOR_DIRECTIONS } from '../models/directions.types';

export const ALL_ICONS_CATEGORY_NAME = 'all-icons';
export const ALL_ICONS_CATEGORIES = [ALL_ICONS_CATEGORY_NAME, ...Object.keys(ICONS_NAMES_GROUPED)];

export function stringToHTML(str: string): ChildNode[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  const bodyChildren = doc.body.childNodes;
  return Array.from(bodyChildren);
}

export function stArray(param: string | string[]) {
  const isArray = Array.isArray(param);
  if (isArray) {
    return param;
  } else {
    const paramTostring = param.replace('{', '').replace('}', '').replace("', '", ',');
    return paramTostring.split(',');
  }
}

export function convertCssClass(param: string) {
  return param.toLowerCase().replace(' ', '-');
}

export function sanitizeString(str?: string): string | undefined {
  return str?.toLowerCase().trim();
}

export const UUId = (): string => uuidv4();

export function getStoryArgTypes(
  argTypes: Record<string, unknown>,
  category: string,
  disabledKeys: string[] = [],
  basedOnArg: string = ''
): Record<string, unknown> {
  return Object.keys(argTypes || {}).reduce(
    (acc, key) => {
      const argType = argTypes[key];

      acc[key] = {
        ...(typeof argType === 'object' && argType !== null ? argType : {}),
        table: { category, disable: disabledKeys.includes(key) },
        ...(basedOnArg ? { if: { arg: basedOnArg, value: true } } : {}),
      };
      return acc;
    },
    {} as Record<string, unknown>
  );
}

export function getValuesAsArrayFromObject(object: { [key: string]: string[] }): string[] {
  return Object.values(object).flat();
}

export function cleanScopedWinkElements(htmlString: string, exclude: string[]): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  function preserveElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    element.removeAttribute('class');
    element.removeAttribute('theme');

    const brand = localStorage.currentBrand || 'telenet';
    if (tagName.startsWith('wink-') && !element.hasAttribute('brand')) {
      element.setAttribute('brand', brand);
    }

    if (exclude.includes(tagName)) {
      Array.from(element.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          preserveElement(child as HTMLElement);
        }
      });
      return true;
    }

    let hasExcludedChildren = false;
    Array.from(element.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE && preserveElement(child as HTMLElement)) {
        hasExcludedChildren = true;
      }
    });

    if (!hasExcludedChildren) {
      element.parentNode?.removeChild(element);
    }

    return hasExcludedChildren;
  }

  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      preserveElement(element);
    }
  });

  let parent: HTMLElement | null = null;

  exclude.forEach((tagName) => {
    const tags = doc.body.querySelectorAll(tagName);
    if (tags[0].parentNode !== doc.body) {
      const temp = Array.from(tags)
        .map((tag) => tag.outerHTML)
        .join('');
      if (parent !== null) {
        parent.innerHTML = temp;
      }
    } else {
      parent = tags[0] as HTMLElement;
    }
  });

  return doc.body.innerHTML;
}

export function zeroheightBaseStory() {
  return {
    globals: { brand: BRANDS.base },
  };
}

export function zeroheightTabascoStory() {
  return {
    globals: { brand: BRANDS.tabasco },
  };
}

export function formatMonthString(date: Date) {
  return date.toLocaleString('default', { month: '2-digit' });
}

export function getDayString() {
  return new Date().toLocaleString('default', { day: '2-digit' });
}

export function getMonthString() {
  return formatMonthString(new Date());
}

export function getYearString(years: number = 0) {
  return (new Date().getFullYear() + years).toString();
}

export function formatToNlDate(date: Date) {
  return date
    .toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    })
    .split(',')[0];
}

export function getCalendarCorrectNotation(language: CalendarLanguages) {
  return CALENDAR_DATE_NOTATIONS[language];
}

export function endOfMonth(dates: string[]) {
  const dayNumbers = dates.map((date) => date.split('-')[0]);

  if (dayNumbers.includes('1')) {
    const index = dayNumbers.indexOf('1');
    if (index === 0) {
      return dates;
    } else if (index === 7) {
      return dates;
    } else if (index > 7) {
      return dates.slice(0, index);
    } else {
      return dates.slice(0, 7);
    }
  }
  return dates;
}

export function getWeekDays(date: Date) {
  const weekDays = [];
  const dayOfWeek = date.getDay();
  const startOfWeek = new Date(date);

  const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(date.getDate() + offset);

  // Populate the weekDays array with all days of the week
  for (let i = 0; i < 14; i++) {
    const currentDay = new Date(startOfWeek);
    currentDay.setDate(startOfWeek.getDate() + i);
    weekDays.push(formatToNlDate(currentDay)); // Get YYYY-MM-DD format
  }

  return endOfMonth(weekDays);
}

export function getNextFiveDays(): string[] {
  const today = new Date();
  const result: string[] = [formatToNlDate(today)];

  for (let i = 1; i <= 5; i++) {
    const nextDay = new Date(today);
    nextDay.setDate(today.getDate() + i);
    const formattedDate = formatToNlDate(nextDay);
    result.push(formattedDate);
  }

  return result;
}

export function categoriesArgTypes(defaultIcon: string = 'bell', category: StoryCategories = STORY_CATEGORIES.cp) {
  return ALL_ICONS_CATEGORIES.reduce(
    (acc, cat) => ({
      ...acc,
      [cat]: {
        name: 'icon',
        control: CONTROL_TYPES.select,
        options: cat === ALL_ICONS_CATEGORY_NAME ? getValuesAsArrayFromObject(ICONS_NAMES_GROUPED) : ICONS_NAMES_GROUPED[cat],
        description: `Available icons for ${cat} category`,
        table: {
          defaultValue: { summary: cat === ALL_ICONS_CATEGORY_NAME ? defaultIcon : ICONS_NAMES_GROUPED[cat][0] },
          category: category,
        },
        if: { arg: 'category', eq: cat },
      },
    }),
    {}
  );
}

export function categoriesArgs(defaultIcon: string = 'bell') {
  return ALL_ICONS_CATEGORIES.reduce((acc, cat) => {
    return {
      ...acc,
      [cat]: cat === ALL_ICONS_CATEGORY_NAME ? defaultIcon : ICONS_NAMES_GROUPED[cat][0],
    };
  }, {});
}

export function getTallestElementHeight(...elements: (HTMLElement | undefined)[]): number {
  let maxHeight = 0;

  elements.forEach((element) => {
    if (element) {
      const height = element.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    }
  });

  return maxHeight;
}

export function assignTheRightActiveTabIndex(key: string): void {
  const supportedKeysMap = new Map<string, () => void>([
    [
      'ArrowRight',
      () => {
        this.handleScroll(INDICATOR_DIRECTIONS.right);
      },
    ],
    [
      'ArrowLeft',
      () => {
        this.handleScroll(INDICATOR_DIRECTIONS.left);
      },
    ],
    [
      'Home',
      () => {
        if (this.activeTabIndex !== 1) {
          this.activeTabIndex = 1;
        }
      },
    ],
    [
      'End',
      () => {
        if (this.activeTabIndex !== this.tabElements.length) {
          this.activeTabIndex = this.tabElements.length;
        }
      },
    ],
  ]);

  const handler = supportedKeysMap.get(key);

  if (handler) {
    handler();
  }
}
